import type { ProductId } from '../types/document'

/**
 * Real world product specs. Once a photo is calibrated these drive actual
 * spacing, so a C9 run on the mockup has bulbs a real twelve inches apart.
 * Before calibration they fall back to a fraction of the photo width.
 */
export interface ProductSpec {
  id: ProductId
  label: string
  /** Bulb glass diameter, inches. */
  bulbInches: number
  /**
   * HOW BIG THE LAMP READS WHEN IT IS LIT, as a multiple of its glass.
   *
   * A lit bulb in a photograph is bigger than the bulb. The glass blooms, the
   * lens scatters, and the sensor clips - which is why a mini light, whose
   * glass is a quarter inch, still reads as a clear dot on a tree from the
   * street. Drawing minis at true glass diameter is what made tree wraps and
   * bushes disappear: on a 1600px photo of a whole house that is barely one
   * pixel, and one pixel of anything is invisible against foliage.
   *
   * C9 is the reference at 1: its size was measured off a real night photo of
   * one of our own rooflines, so it already includes its own bloom.
   */
  litScale: number
  /** Socket to socket spacing on the wire, inches. */
  spacingInches: number
  /** How hot the filament reads. Multiplies the core brightness. */
  intensity: number
  /** Wire color as installed. */
  wire: string
  blurb: string
}

export const PRODUCTS: Record<ProductId, ProductSpec> = {
  C9: {
    id: 'C9',
    label: 'C9',
    bulbInches: 1.13,
    /*
     * Measured, not chosen: on the reference night photograph the bulbs sit
     * 50px apart on a straight eave and the blown out core of each is 17px
     * across. At 12 inch spacing that is 4.17px to the inch, so a lit C9
     * covers about 4 inches where its glass is 1.13. A filament that much
     * brighter than the scene clips the sensor well beyond the glass.
     */
    litScale: 2.4,
    spacingInches: 12,
    intensity: 1,
    wire: '#14351C',
    blurb: 'The classic roofline bulb. Big, warm, reads from the street.',
  },
  C7: {
    id: 'C7',
    label: 'C7',
    bulbInches: 0.88,
    // Same bloom, smaller glass, so it reads a little smaller than a C9.
    litScale: 2.6,
    spacingInches: 12,
    intensity: 0.9,
    wire: '#14351C',
    blurb: 'Slightly smaller than a C9. Good on smaller single storey homes.',
  },
  mini: {
    id: 'mini',
    label: 'Mini',
    bulbInches: 0.28,
    /*
     * A quarter inch of glass, four inches apart. Held to the same third of
     * the gap that the reference photograph shows, which takes a great deal
     * more bloom than a C9 needs - and that is right, because the smaller the
     * source the more of what you see is bloom rather than glass.
     */
    litScale: 5,
    spacingInches: 4,
    intensity: 0.72,
    wire: '#1A2416',
    blurb: 'Tree wraps, bushes and palm trunks. Dense and sparkly.',
  },
  permanent: {
    id: 'permanent',
    label: 'Permanent',
    bulbInches: 0.62,
    litScale: 2.2,
    spacingInches: 4,
    intensity: 0.85,
    wire: '#2A2A2C',
    blurb: 'Year round track lighting. Tight even spacing, low profile.',
  },
}

export const PRODUCT_ORDER: ProductId[] = ['C9', 'C7', 'mini', 'permanent']
